import { useEffect, useRef, useState, useContext } from "react";
import { getGeneralData } from "../../../../Utility/Api";
import { postGeneralDataNew } from "../../../../Utility/Api";

import "bootstrap/dist/css/bootstrap.min.css";
import { currentMonth, handlePrint, NaviGate, shop_id, getStoredEntriesPerPage } from "../../../../Utility/function";
import { Paginations } from "../../../Common/Functions";
import { toast } from "react-toastify";
import { WarehouseContext } from "../../../../Utility/UserContext";
import { Printer } from "lucide-react";

// Helper function to get current week range
function getCurrentWeek() {
  const today = new Date();
  const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
  const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));

  return {
    start: firstDay.toISOString().split('T')[0],
    end: lastDay.toISOString().split('T')[0]
  };
}

// Helper function to get week range from date
function getWeekRange(dateString) {
  const date = new Date(dateString);
  const firstDay = new Date(date.setDate(date.getDate() - date.getDay()));
  const lastDay = new Date(date.setDate(date.getDate() - date.getDay() + 6));

  return {
    start: firstDay.toISOString().split('T')[0],
    end: lastDay.toISOString().split('T')[0]
  };
}

const GeneralLedger = () => {
  const contentRef = useRef(null);
  const { shopItem } = useContext(WarehouseContext);
  const [filterType, setFilterType] = useState(() => {
    return sessionStorage.getItem("gl_filterType") || "daily";
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    return sessionStorage.getItem("gl_selectedDate") || new Date().toISOString().split('T')[0];
  });
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const saved = sessionStorage.getItem("gl_selectedWeek");
    return saved ? JSON.parse(saved) : getCurrentWeek();
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return sessionStorage.getItem("gl_selectedMonth") || currentMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    return sessionStorage.getItem("gl_selectedYear") || new Date().getFullYear().toString();
  });
  const [customFromDate, setCustomFromDate] = useState(() => {
    return sessionStorage.getItem("gl_customFromDate") || "";
  });
  const [customToDate, setCustomToDate] = useState(() => {
    return sessionStorage.getItem("gl_customToDate") || "";
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [allTransactions, setAllTransactions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    sessionStorage.setItem("gl_filterType", filterType);
    sessionStorage.setItem("gl_selectedDate", selectedDate);
    sessionStorage.setItem("gl_selectedWeek", JSON.stringify(selectedWeek));
    sessionStorage.setItem("gl_selectedMonth", selectedMonth);
    sessionStorage.setItem("gl_selectedYear", selectedYear);
    sessionStorage.setItem("gl_customFromDate", customFromDate);
    sessionStorage.setItem("gl_customToDate", customToDate);
  }, [filterType, selectedDate, selectedWeek, selectedMonth, selectedYear, customFromDate, customToDate]);

  useEffect(() => {
    updateDateRange();
  }, [filterType, selectedDate, selectedWeek, selectedMonth, selectedYear, customFromDate, customToDate]);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchTransactionData();
    }
  }, [fromDate, toDate]);

  const updateDateRange = () => {
    let startDate = "";
    let endDate = "";

    switch (filterType) {
      case "daily":
        startDate = selectedDate;
        endDate = selectedDate;
        break;
      case "weekly":
        startDate = selectedWeek.start;
        endDate = selectedWeek.end;
        break;
      case "monthly":
        startDate = `${selectedMonth}-01`;
        const lastDay = new Date(
          new Date(startDate).getFullYear(),
          new Date(startDate).getMonth() + 1,
          0
        ).getDate();
        endDate = `${selectedMonth}-${lastDay}`;
        break;
      case "yearly":
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
        break;
      case "custom":
        startDate = customFromDate;
        endDate = customToDate;
        break;
      default:
        break;
    }

    setFromDate(startDate);
    setToDate(endDate);
  };

  const fetchTransactionData = async () => {
    try {
      var filter = `start_date=${fromDate}&end_date=${toDate}&shop_id=${shop_id}`;
      const res = await getGeneralData(filter);
      if (res.data && res.data.data) {
        setAllTransactions(res.data.data);
      }
    } catch (err) {
      console.log("Transaction Error", err);
    }
  };

  const handleWeekChange = (dateString) => {
    setSelectedWeek(getWeekRange(dateString));
  };

  const filteredTransactions = allTransactions.filter((item) => {
    if (item.status === "-1" || item.status === "2") return false;
    if (!item.amount) return false;

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const dateMatch = item.date && item.date.toLowerCase().includes(searchLower);
      const refIdMatch = item.ref_id && item.ref_id.toLowerCase().includes(searchLower);
      const remarksMatch = item.remarks && item.remarks.toLowerCase().includes(searchLower);
      const amountMatch = item.amount && item.amount.toLowerCase().includes(searchLower);
      return dateMatch || refIdMatch || remarksMatch || amountMatch;
    }
    return true;
  });

  const handlePrintAll = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Please allow popups to print statements.");
      return;
    }

    const companyInfo = {
      name: shopItem?.title || "Company Name",
      address: shopItem?.Address || "123 Business Street, City, State 12345",
      phone: shopItem?.phone1 || "",
      email: shopItem?.email || ""
    };

    const tableRows = filteredTransactions
      .map((item) => ({
        id: item.id, // new line added for id sort
        date: item.date,
        ref_id: item.ref_id,
        remarks: item.remarks,
        debit: item.is_debit == "1" ? parseFloat(item.amount) : 0,
        credit: item.is_debit == "0" ? parseFloat(item.amount) : 0,
      }))
      // .sort((a, b) => a.id - b.id)

      .sort((a, b) => {
        const dateA = new Date(a.date).getTime() || 0;
        const dateB = new Date(b.date).getTime() || 0;
        if (dateB !== dateA) return dateB - dateA;
        return a.id - b.id;
      })
      .map((item, index) => `
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 12px; text-align: center; color: #475569;">${index + 1}</td>
          <td style="padding: 12px; text-align: center; color: #475569;">${item.date}</td>
          <td style="padding: 12px; text-align: center; font-weight: 500; color: #1e293b;">${item.ref_id || "-"}</td>
          <td style="padding: 12px; text-align: left; color: #475569;">${item.remarks || "-"}</td>
          <td style="padding: 12px; text-align: right; color: #10b981; font-weight: bold;">
            ${item.debit > 0 ? `₹ ${item.debit.toFixed(2)}` : "-"}
          </td>
          <td style="padding: 12px; text-align: right; color: #ef4444; font-weight: bold;">
            ${item.credit > 0 ? `₹ ${item.credit.toFixed(2)}` : "-"}
          </td>
        </tr>
      `).join('');

    const totalDebit = filteredTransactions
      .reduce((sum, item) => sum + (item.is_debit == "1" ? parseFloat(item.amount || 0) : 0), 0);
    const totalCredit = filteredTransactions
      .reduce((sum, item) => sum + (item.is_debit == "0" ? parseFloat(item.amount || 0) : 0), 0);
    const netBalance = totalDebit - totalCredit;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>General Ledger - ${companyInfo.name}</title>
        <style>
          @media print {
            @page {
              size: A4 portrait;
              margin: 15mm;
            }
          }
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            color: #1e293b;
            background: #fff;
            margin: 0;
            padding: 20px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header-title {
            font-size: 24px;
            font-weight: bold;
            color: #0f172a;
            margin: 0;
          }
          .company-name {
            font-size: 18px;
            font-weight: 600;
            color: #2563eb;
            margin: 5px 0 0 0;
          }
          .company-details {
            font-size: 12px;
            color: #64748b;
            margin: 3px 0;
          }
          .statement-info {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            margin-bottom: 25px;
            background-color: #f8fafc;
            padding: 12px;
            border-radius: 8px;
            border: 1px solid #f1f5f9;
          }
          .summary-cards {
            display: flex;
            gap: 15px;
            margin-bottom: 25px;
          }
          .card {
            flex: 1;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 12px;
            background-color: #fff;
          }
          .card-label {
            font-size: 11px;
            text-transform: uppercase;
            font-weight: 600;
            color: #64748b;
            margin-bottom: 5px;
          }
          .card-value {
            font-size: 16px;
            font-weight: 700;
          }
          .table-container {
            width: 100%;
            margin-bottom: 30px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th {
            background-color: #f1f5f9;
            color: #475569;
            font-weight: 600;
            padding: 12px;
            text-align: center;
            border-bottom: 2px solid #cbd5e1;
          }
          td {
            padding: 12px;
            border-bottom: 1px solid #e2e8f0;
          }
          .text-right {
            text-align: right;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 11px;
            color: #94a3b8;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              <h1 class="header-title">General Ledger Report</h1>
              <p class="company-name">${companyInfo.name}</p>
              <p class="company-details">${companyInfo.address}</p>
              <p class="company-details">Phone: ${companyInfo.phone} | Email: ${companyInfo.email}</p>
            </div>
            <div style="text-align: right; font-size: 12px; color: #64748b;">
              <p style="margin: 0;"><strong>Date range:</strong> ${fromDate} to ${toDate}</p>
              <p style="margin: 5px 0 0 0;"><strong>Generated On:</strong> ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}</p>
            </div>
          </div>
        </div>

        <div class="statement-info">
          <div>
            <strong>Report Type:</strong> General Ledger Summary
          </div>
          <div>
            <strong>Generated On:</strong> ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}
          </div>
        </div>

        <div class="summary-cards">
          <div class="card" style="border-left: 4px solid #10b981;">
            <div class="card-label">Total Debit</div>
            <div class="card-value" style="color: #059669;">₹ ${totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div class="card" style="border-left: 4px solid #ef4444;">
            <div class="card-label">Total Credit</div>
            <div class="card-value" style="color: #b91c1c;">₹ ${totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div class="card" style="border-left: 4px solid #3b82f6;">
            <div class="card-label">Net Balance</div>
            <div class="card-value" style="color: #1d4ed8;">₹ ${netBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th style="width: 8%">S.No</th>
                <th style="width: 15%">Date</th>
                <th style="width: 25%">Reference ID</th>
                <th style="width: 32%">Remarks</th>
                <th style="width: 10%; text-align: right;">Debit (INR)</th>
                <th style="width: 10%; text-align: right;">Credit (INR)</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows.length > 0 ? tableRows : `<tr><td colspan="6" style="text-align: center; padding: 20px; color: #94a3b8;">No transactions recorded for this period.</td></tr>`}
            </tbody>
          </table>
        </div>

        <div class="footer">
          Thank you for choosing ${companyInfo.name}. This is a computer-generated general ledger report.
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 100);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const renderFilterControls = () => {
    switch (filterType) {
      case "daily":
        return (
          <div className="col-auto">
            <div className="form-floating">
              <input
                type="date"
                className="form-control"
                id="selectedDate"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <label htmlFor="selectedDate">Select Date</label>
            </div>
          </div>
        );
      case "weekly":
        return (
          <div className="col-auto">
            <div className="form-floating">
              <input
                type="date"
                className="form-control"
                id="weekDate"
                value={selectedWeek.start}
                onChange={(e) => handleWeekChange(e.target.value)}
              />
              <label htmlFor="weekDate">Select Week</label>
            </div>
          </div>
        );
      case "monthly":
        return (
          <div className="col-auto">
            <div className="form-floating">
              <input
                type="month"
                className="form-control"
                id="selectedMonth"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
              <label htmlFor="selectedMonth">Select Month</label>
            </div>
          </div>
        );
      case "yearly":
        return (
          <div className="col-auto">
            <div className="form-floating">
              <input
                type="number"
                className="form-control"
                id="selectedYear"
                min="2000"
                max="2100"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              />
              <label htmlFor="selectedYear">Select Year</label>
            </div>
          </div>
        );
      case "custom":
        return (
          <>
            <div className="col-auto">
              <div className="form-floating">
                <input
                  type="date"
                  className="form-control"
                  id="customFromDate"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                />
                <label htmlFor="customFromDate">From Date</label>
              </div>
            </div>
            <div className="col-auto">
              <div className="form-floating">
                <input
                  type="date"
                  className="form-control"
                  id="customToDate"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                />
                <label htmlFor="customToDate">To Date</label>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-content py-1">
      <NaviGate />
      <div className="card p-3 mx-2 overflow-y-scroll" style={{ minHeight: "90vh" }} ref={contentRef}>
        <div className="row mb-4 border-bottom justify-content-between align-items-center w-100 pb-3">
          <h5 className="text-start w-auto fw-bold mb-0">
            General Ledger
          </h5>
          <div className="w-auto d-flex gap-2 mt-0 justify-content-end align-items-center">
            <div className="col-auto">
              <div className="form-floating">
                <select
                  className="form-select"
                  id="filterType"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="custom">Custom Range</option>
                </select>
                <label htmlFor="filterType">Filter By</label>
              </div>
            </div>

            {renderFilterControls()}

            <button
              type="button"
              className="btn btn-outline-primary fw-bold d-flex align-items-center gap-2"
              onClick={handlePrintAll}
              style={{ height: "58px" }}
            >
              <Printer size={18} />
              Print
            </button>

            <button
              type="button"
              className="btn btn-primary"
              style={{ height: "58px", display: "flex", alignItems: "center" }}
              onClick={() => setShowAddModal(true)}
            >
              + Add Ledger
            </button>

          </div>
        </div>

        <div className="row justify-content-between align-items-center mb-3">
          <div className="col-auto text-secondary">
            <strong>Date Range:</strong> {fromDate} to {toDate}
          </div>
          <div className="col-md-3 mb-3">
            <div className="form-floating">
              <input
                type="text"
                className="form-control"
                id="searchLedger"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <label htmlFor="searchLedger">Search Ledger</label>
            </div>
          </div>
        </div>

        <div className="row g-4 pt-0">
          <div className="col-md-12 mt-2">
            <TransactionTable
              transactions={filteredTransactions}
              dateRange={`${fromDate} to ${toDate}`}
            />
          </div>
        </div>

        <AddGeneralLedgerModal
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => fetchTransactionData()}
        />

      </div>
    </div>
  );
};

const TransactionTable = ({ transactions, dateRange }) => {
  const [transactionData, setTransactionData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(getStoredEntriesPerPage(50));

  const totalPages = Math.ceil(transactionData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = transactionData.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    const updatedTransactionData = transactions
      .filter((item) => item.status !== "-1" && item.status !== "2")
      .map((item) => {
        if (item.amount) {
          return {
            id: item.id,
            date: item.date,
            ref_id: item.ref_id,
            remarks: item.remarks,
            debit: item.is_debit == "1" ? item.amount : "0.0000",
            credit: item.is_debit == "0" ? item.amount : "0.0000",
          }
        }
        return null;
      })
      .filter(item => item !== null);

    //       // Sort ascending by ID so the first entry appears first
    // updatedTransactionData.sort((a, b) => a.id - b.id);


    // Sort descending by date (recent on top), keeping original ID order within same date
    updatedTransactionData.sort((a, b) => {
      const dateA = new Date(a.date).getTime() || 0;
      const dateB = new Date(b.date).getTime() || 0;
      if (dateB !== dateA) return dateB - dateA;
      return a.id - b.id;
    });

    setTransactionData(updatedTransactionData);
  }, [transactions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [transactions]);

  // Calculate totals
  const totalDebit = transactionData.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0);
  const totalCredit = transactionData.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0);

  return (
    <>
      <div className="p-0 bg-white w-full">
        <div className="w-full bg-white shadow-md rounded-lg overflow-hidden">
          <h4 className="text-xl font-bold text-center bg-dark text-white py-2 mb-0">General Ledger</h4>

          <table className="w-full table border-collapse text-sm table-hover table-bordered mb-0">
            <thead>
              <tr className="bg-green text-white">
                <th className="py-2 px-4 text-center">Date</th>
                <th className="py-2 px-4 text-center">Reference ID</th>
                <th className="py-2 px-4 text-center">Remarks</th>
                <th className="py-2 px-4 text-center">Debit (INR)</th>
                <th className="py-2 px-4 text-center">Credit (INR)</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => (
                  <tr key={index} className={`border-b ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}>
                    <td className="py-2 px-4 text-center">{item?.date}</td>
                    <td className="py-2 px-4 text-center">{item?.ref_id}</td>
                    <td className="py-2 px-4 text-center">{item?.remarks}</td>
                    <td className="py-2 px-4 text-end text-green-600 font-medium">{parseFloat(item?.debit || 0).toFixed(3)}</td>
                    <td className="py-2 px-4 text-end text-green-600 font-medium">{parseFloat(item?.credit || 0).toFixed(3)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    No transactions found for this period.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-light d-none">
                <td colSpan="3" className="py-2 px-4 text-end fw-bold">Page Totals:</td>
                <td className="py-2 px-4 text-end fw-bold">
                  {paginatedData.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0).toFixed(3)}
                </td>
                <td className="py-2 px-4 text-end fw-bold">
                  {paginatedData.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0).toFixed(3)}
                </td>
              </tr>
            </tfoot>
          </table>

          {transactionData.length > 0 && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              entriesPerPage={rowsPerPage}
              onEntriesPerPageChange={(val) => {
                setRowsPerPage(val);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

const AddGeneralLedgerModal = ({ show, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: "",
    ref_id: "",
    remarks: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    paid_amount: "",
    cogs_amount: "",
    is_debit: "1",
    profitloss: "0",
  });

  useEffect(() => {
    if (show) {
      setFormData({
        type: "",
        ref_id: "",
        remarks: "",
        date: new Date().toISOString().split("T")[0],
        amount: "",
        paid_amount: "",
        cogs_amount: "",
        is_debit: "1",
        profitloss: "0",
      });
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      if (name === "type") {
        // Auto debit/credit based on type
        if (value === "8" || value === "11" || value === "6" || value === "3" || value === "5") {
          updated.is_debit = "1"; // Debit
        }
        if (value === "7" || value === "12") {
          updated.is_debit = "0"; // Credit
        }
      }

      return updated;
    });
  };


  const handleSubmit = async () => {
    if (!formData.amount) {
      toast.error("Please enter an amount");
      return;
    }

    try {
      const payload = {
        ...formData,
        shop_id: shop_id,
        created_by: "1", // replace with logged-in user id
      };

      const res = await postGeneralDataNew(payload);

      if (res.data && res.data.status === "success") {
        toast.success("Entry added successfully");
        onSuccess();
        onClose();
      } else {
        toast.error(res.data.message || "Failed to add entry");
      }
    } catch (err) {
      console.error("Add ledger error", err);
      const errorMsg = err.response?.data?.message || "Failed to add ledger";
      toast.error(errorMsg);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header bg-success text-white">
            <h5 className="modal-title">Add General Ledger</h5>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="form-group mb-2">
              <label>Ledger Type</label>
              <select
                className="form-select"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="">Select Type</option>
                <option value="6">Expense</option>
                <option value="3">Stock Purchase</option>
                <option value="5">Sales</option>
              </select>
            </div>


            <div className="form-group mb-2">
              <label>Reference ID</label>
              <input
                type="text"
                className="form-control"
                name="ref_id"
                value={formData.ref_id}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mb-2">
              <label>Date</label>
              <input
                type="date"
                className="form-control"
                name="date"
                value={formData.date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mb-2">
              <label>Amount</label>
              <input
                type="number"
                className="form-control"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
              />
            </div>

            {(formData.type === "3" || formData.type === "5") && (
              <div className="form-group mb-2">
                <label>Paid Amount</label>
                <input
                  type="number"
                  className="form-control"
                  name="paid_amount"
                  value={formData.paid_amount}
                  onChange={handleChange}
                />
              </div>
            )}

            {formData.type === "5" && (
              <div className="form-group mb-2">
                <label>COGS Amount</label>
                <input
                  type="number"
                  className="form-control"
                  name="cogs_amount"
                  value={formData.cogs_amount}
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="form-group mb-2">
              <label>Debit / Credit</label>
              <select
                className="form-select"
                name="is_debit"
                value={formData.is_debit}
                onChange={handleChange}
                disabled={formData.type === "6"}
              >
                <option value="1">Debit</option>
                {formData.type !== "6" && <option value="0">Credit</option>}
              </select>
            </div>

            <div className="form-group mb-2">
              <label>Remarks</label>
              <input
                type="text"
                className="form-control"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-success" onClick={handleSubmit}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default GeneralLedger;